// Fire-and-forget audit event POST. Called from places where the user
// changes state that's otherwise client-only (localStorage flags,
// feature toggles, in-page UI choices) so the change still appears in
// the Logs page.
//
// The BE side is at POST /admin/logs/event and lets the auto-logging
// middleware do the actual row insert via res.locals.logCtx — so this
// helper just packages a department/action/metadata triple and the
// signed-in user's identity, then forgets about it. Errors are
// swallowed: a logging failure must never break a UI interaction.

import { apiFetch } from "../../api/apiFetch";

interface AuditEvent {
    department: string;
    action: string;
    metadata?: Record<string, unknown>;
}

interface SessionUser {
    UID?: number;
    uid?: number;
    name?: string;
    warehouse?: string;
}

const readUser = (): SessionUser | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
        return null;
    }
};

export const fireAuditEvent = ({ department, action, metadata }: AuditEvent): void => {
    if (typeof window === "undefined") return;
    const user = readUser();
    // The BE middleware identifies the user by inspecting req.body.{uid,name,warehouse}
    // (or the uppercase variants). Sending both lets the middleware
    // resolve the identity without a Mongo lookup.
    const body: Record<string, unknown> = { department, action };
    if (metadata) body.metadata = metadata;
    if (user) {
        if (typeof user.UID === "number") body.uid = user.UID;
        else if (typeof user.uid === "number") body.uid = user.uid;
        if (typeof user.name === "string") body.name = user.name;
        if (typeof user.warehouse === "string") body.warehouse = user.warehouse;
    }
    apiFetch(`/admin/logs/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        // keepalive lets the request survive a fast page navigation —
        // useful if a user toggles a setting and immediately leaves.
        keepalive: true,
    }).catch(() => {
        // Audit failures are silent. UI must not be affected.
    });
};
